/**
 * A control chart in the org's units (units slice 2b). The server draws every chart in the unit its tag stores; an org
 * that reads in another unit sees the readings, the centre line and the limits move into it together (and the axis unit
 * with them), never the data in one unit under a label in the other. Pure.
 *
 * What converts is what sits on the readings' scale: a location chart's points and their limits, an EWMA statistic and
 * its limits. A rate chart (scrap rates, no unit of its own) and a CUSUM (its sums are not on the readings' scale) are
 * left as the server sent them. Where nothing converts it is the very same object.
 */
import { shownFor, spreadOf } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import type { SpcChartType, SpcSeries } from "./spc";

const num = (convert: (v: number) => number, v: number | null) => (v === null ? null : convert(v));

/** The chart types whose points are readings of the tag, on its scale. */
const ON_READINGS_SCALE: readonly SpcChartType[] = ["imr", "xbar_r", "xbar_s", "ewma"];

/** A chart's series, with the unit it is then drawn in; the series as given when nothing converts (and its unit as given). */
export function spcSeriesInUnit(
  series: SpcSeries,
  storedUnit: string,
  prefs: DisplayPrefs | undefined,
): { series: SpcSeries; unit: string } {
  const s = shownFor(storedUnit, prefs);
  if (!s.converted || series.chart.subject_kind !== "tag" || !ON_READINGS_SCALE.includes(series.chart.chart_type)) {
    return { series, unit: storedUnit };
  }
  const c = s.convert;
  const out: SpcSeries = {
    ...series,
    limits: {
      ...series.limits,
      center: num(c, series.limits.center),
      ucl: num(c, series.limits.ucl),
      lcl: num(c, series.limits.lcl),
    },
    points: series.points.map((p) => ({
      ...p,
      value: num(c, p.value),
      center: c(p.center),
      ucl: c(p.ucl),
      lcl: c(p.lcl),
    })),
  };
  if (series.statistic) {
    out.statistic = {
      ...series.statistic,
      points: series.statistic.points.map((p) => ({ ...p, value: c(p.value), ucl: c(p.ucl), lcl: c(p.lcl) })),
    };
  }
  return { series: out, unit: s.unit };
}

/** A golden reference ({mean, sigma}, stored in the tag's unit) in the shown unit: the mean moves, the sigma scales. */
export function spcReferenceInUnit<R extends { mean: number; sigma: number }>(
  reference: R,
  storedUnit: string,
  prefs: DisplayPrefs | undefined,
): R;
export function spcReferenceInUnit<R extends { mean: number; sigma: number }>(
  reference: R | undefined,
  storedUnit: string,
  prefs: DisplayPrefs | undefined,
): R | undefined;
export function spcReferenceInUnit<R extends { mean: number; sigma: number }>(
  reference: R | undefined,
  storedUnit: string,
  prefs: DisplayPrefs | undefined,
): R | undefined {
  if (!reference) return reference;
  const s = shownFor(storedUnit, prefs);
  if (!s.converted) return reference;
  return { ...reference, mean: s.convert(reference.mean), sigma: spreadOf(reference.sigma, reference.mean, storedUnit, prefs) };
}
